// =============================================================
// VersionBadge
// -------------------------------------------------------------
// Tiny chip that shows the running app version + last-updated
// date/time, pulled from the VersionContext. Designed to live in
// the sidebar footer of both the tenant and super-admin layouts.
//
// Clicking the chip opens a popover with the release notes so the
// admin can see what changed since the last deployment.
//
// All text is rendered in white so the chip stays legible against
// the dark sidebar background.
// =============================================================

import React, { useState, useRef, useEffect } from 'react';
import { useVersion } from '../../context/VersionContext';

const COLORS = {
  // Light foregrounds (white) for sidebar legibility
  text:        '#ffffff',
  textMuted:   'rgba(255, 255, 255, 0.72)',
  textDim:     'rgba(255, 255, 255, 0.55)',
  border:      'rgba(255, 255, 255, 0.18)',
  borderHover: 'rgba(255, 255, 255, 0.32)',
  bg:          'rgba(255, 255, 255, 0.06)',
  bgHover:     'rgba(255, 255, 255, 0.10)',
  accent:      '#60a5fa',
};

export default function VersionBadge({ collapsed = false, superAdmin = false }) {
  const v = useVersion();
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(false);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const verLabel     = v.version || '1.0.0';
  const buildLabel   = v.buildNumber ? ` · build ${v.buildNumber}` : '';
  const lastUpdated  = v.formattedDate || (v.lastUpdatedAt ? new Date(v.lastUpdatedAt).toLocaleString('en-IN') : '—');
  const hasNotes     = !!v.releaseNotes;

  // Sidebar-collapsed: render just an icon button with a tooltip-style popover.
  if (collapsed) {
    return (
      <div ref={ref} style={{ position: 'relative', marginTop: 4 }}>
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          title={`v${verLabel}${buildLabel}`}
          style={{
            width: '100%',
            background: hover ? COLORS.bgHover : COLORS.bg,
            border: `1px solid ${hover ? COLORS.borderHover : COLORS.border}`,
            borderRadius: 6,
            color: COLORS.text,
            fontSize: 10,
            padding: '4px 6px',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
            cursor: 'pointer',
            transition: 'background .15s ease, border-color .15s ease',
          }}
        >
          <i className="bi bi-tag" style={{ fontSize: 11, color: COLORS.text }} />
          <span style={{ fontWeight: 700, fontFamily: 'monospace', color: COLORS.text }}>v{verLabel}</span>
        </button>
        {open && (
          <div style={{
            position: 'absolute', left: 'calc(100% + 8px)', bottom: 0,
            background: '#0f172a', color: COLORS.text,
            padding: '8px 10px', borderRadius: 6,
            boxShadow: '0 4px 16px rgba(0,0,0,0.30)',
            fontSize: 11, minWidth: 200, maxWidth: 280, zIndex: 9999,
            whiteSpace: 'normal',
          }}>
            <div style={{ fontWeight: 700, marginBottom: 4, color: COLORS.text }}>v{verLabel}{buildLabel}</div>
            <div style={{ color: COLORS.textMuted, fontSize: 10, marginBottom: hasNotes ? 6 : 0 }}>
              Updated {lastUpdated}
            </div>
            {hasNotes && (
              <div style={{ borderTop: '1px solid #334155', paddingTop: 6, fontSize: 10, color: COLORS.textMuted }}>
                {v.releaseNotes}
              </div>
            )}
            {superAdmin && (
              <div style={{ borderTop: '1px solid #334155', paddingTop: 6, marginTop: 6, fontSize: 10 }}>
                <a href="/sa/app-version" style={{ color: COLORS.accent }}>
                  <i className="bi bi-gear me-1" />Manage version
                </a>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div ref={ref} style={{ position: 'relative', marginTop: 'auto' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          width: '100%',
          background: hover ? COLORS.bgHover : COLORS.bg,
          border: `1px solid ${hover ? COLORS.borderHover : COLORS.border}`,
          borderRadius: 8,
          color: COLORS.text,
          padding: '8px 10px',
          display: 'flex', alignItems: 'center', gap: 10,
          cursor: 'pointer',
          textAlign: 'left',
          transition: 'background .15s ease, border-color .15s ease',
        }}
        title={`Updated ${lastUpdated}`}
      >
        <i className="bi bi-tag-fill" style={{ fontSize: 14, color: COLORS.text }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.text }}>
            v{verLabel}{buildLabel}
          </div>
          <div style={{ fontSize: 10, color: COLORS.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {lastUpdated}
          </div>
        </div>
        <i className="bi bi-chevron-up" style={{ fontSize: 10, color: COLORS.text, opacity: open ? 1 : 0.6 }} />
      </button>
      {open && (
        <div style={{
          position: 'absolute', left: 0, right: 0, bottom: 'calc(100% + 6px)',
          background: '#0f172a', color: COLORS.text,
          padding: '10px 12px', borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,0.30)',
          fontSize: 11, zIndex: 9999,
        }}>
          <div style={{ fontWeight: 700, marginBottom: 4, color: COLORS.text }}>
            Application v{verLabel}{buildLabel}
          </div>
          <div style={{ color: COLORS.textMuted, fontSize: 10, marginBottom: hasNotes ? 6 : 0 }}>
            <i className="bi bi-clock-history me-1" />Last updated: {lastUpdated}
          </div>
          {hasNotes && (
            <div style={{ borderTop: '1px solid #334155', paddingTop: 6, fontSize: 10, color: COLORS.textMuted, maxHeight: 120, overflowY: 'auto' }}>
              <div style={{ fontWeight: 600, color: COLORS.accent, marginBottom: 2 }}>Release notes</div>
              <div style={{ whiteSpace: 'pre-wrap' }}>{v.releaseNotes}</div>
            </div>
          )}
          {superAdmin && (
            <div style={{ borderTop: '1px solid #334155', paddingTop: 6, marginTop: 6 }}>
              <a href="/sa/app-version" style={{ color: COLORS.accent, fontSize: 10 }}>
                <i className="bi bi-gear me-1" />Manage version
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
